/**
 * Owner design chrome stays off until the owner asks for it.
 *
 * A cold / anonymous visit matches the public storefront (Lighthouse).
 * `?edit=1`, `?admin=edit`, or `?studio=app` opens the studio in edit mode.
 * Once the owner picks Edit or View as, that choice is remembered in a
 * cookie (and sessionStorage) so SSR and the first client paint agree —
 * owner tools, Support, and page lessons ship in the first HTML instead of
 * popping in after hydration.
 */

/** The owner's current storefront mode. `null` = never armed, plain visitor. */
export type StoreOwnerMode = 'edit' | 'view'

const OWNER_MODE_KEY = 'cs.storeOwnerMode.v1'

function queryWantsEditor(query: Record<string, unknown> | undefined): boolean {
  const edit = String(query?.edit ?? '')
  const admin = String(query?.admin ?? '')
  const studio = String(query?.studio ?? '')
  return edit === '1' || edit === 'true' || admin === 'edit' || admin === 'live' || studio === 'app'
}

/** True when this URL is an explicit open of the design studio in edit mode. */
function queryOpensStudioEdit(query: Record<string, unknown> | undefined): boolean {
  const edit = String(query?.edit ?? '')
  const admin = String(query?.admin ?? '')
  const studio = String(query?.studio ?? '')
  return edit === '1' || edit === 'true' || admin === 'edit' || studio === 'app'
}

function useOwnerModeCookie() {
  return useCookie<StoreOwnerMode | null>(OWNER_MODE_KEY, {
    default: () => null,
    sameSite: 'lax',
    // Session cookie: gone when the browser session ends (matches tab intent).
  })
}

function normalizeOwnerMode(value: unknown): StoreOwnerMode | null {
  return value === 'edit' || value === 'view' ? value : null
}

/**
 * Land in edit mode. A leftover or remembered View as must not win when the
 * studio is opened — `useState` survives the trip from the creator hub.
 */
export function enterStudioEditMode(query: Record<string, unknown> | undefined): boolean {
  if (!queryOpensStudioEdit(query)) return false
  const { editorArmed } = useStoreEditorArmed()
  editorArmed.value = true
  try {
    useStoreLivePreview().value = false
  }
  catch {
    // Called outside a Nuxt instance.
  }
  return true
}

export function readStoreOwnerMode(): StoreOwnerMode | null {
  try {
    const fromCookie = normalizeOwnerMode(useOwnerModeCookie().value)
    if (fromCookie) return fromCookie
  }
  catch {
    // Outside Nuxt — fall through to sessionStorage.
  }
  if (!import.meta.client) return null
  try {
    return normalizeOwnerMode(sessionStorage.getItem(OWNER_MODE_KEY))
  }
  catch {
    return null
  }
}

export function writeStoreOwnerMode(mode: StoreOwnerMode | null): void {
  try {
    useOwnerModeCookie().value = mode
  }
  catch {
    // Outside Nuxt.
  }
  if (!import.meta.client) return
  try {
    if (!mode) sessionStorage.removeItem(OWNER_MODE_KEY)
    else sessionStorage.setItem(OWNER_MODE_KEY, mode)
  }
  catch {
    // Private mode / quota — cookie + in-memory state still cover this nav.
  }
}

/**
 * Re-apply the remembered mode after hydration when SSR had no cookie yet
 * (legacy sessionStorage-only tabs). Prefer the cookie path so SSR already
 * matches; this is then a no-op.
 */
export function restoreStoreOwnerMode(): void {
  const route = useRoute()
  // Opening the studio (`?edit=1`, `?admin=edit`, `?studio=app`) is edit mode,
  // even when this tab last left the storefront in View as.
  if (enterStudioEditMode(route.query as Record<string, unknown>)) return

  const { editorArmed } = useStoreEditorArmed()
  const livePreview = useStoreLivePreview()

  // Already armed (query, cookie SSR, or in-session): keep the choice.
  if (editorArmed.value) return

  const mode = readStoreOwnerMode()
  if (!mode) return
  editorArmed.value = true
  livePreview.value = mode === 'view'
}

export function useStoreEditorArmed() {
  const route = useRoute()
  const armed = useState('store-editor-armed', () => {
    if (queryWantsEditor(route.query as Record<string, unknown>)) return true
    const mode = readStoreOwnerMode()
    return mode === 'edit' || mode === 'view'
  })

  function armEditor() {
    armed.value = true
    try {
      useStoreLivePreview().value = false
    }
    catch {
      // ignore
    }
  }

  function disarmEditor() {
    armed.value = false
  }

  return { editorArmed: armed, armEditor, disarmEditor }
}
